import { Form, Input, Button, Row, Col, Select } from "antd";
import { useKycForm } from "../hooks/useKycForm";
import React, { useState, useEffect } from "react";
import { Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import axios from "axios";
import { ENDPOINTS } from "../../../../constants/endpoints.constant";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";

const { Dragger } = Upload;

const { Option } = Select;

interface KycFormProps {
  isEditMode?: boolean;
  onCancel?: () => void;
}

const KycForm: React.FC<KycFormProps> = ({ isEditMode = false, onCancel }) => {
  const user = useSelector((state: RootState) => state.user);
  const { bankDetails, bankLoading, form, handleSubmit } = useKycForm({
    isEditMode,
    onEditComplete: onCancel,
  });
  const [editMode] = useState(isEditMode);

  // Initialize form with existing data when in edit mode
  useEffect(() => {
    if (isEditMode && user?.pendingKycRequest && form) {
      const kycData = user.pendingKycRequest;
      form.setFieldsValue({
        pan_name: kycData?.pan_name,
        pan_number: kycData?.pan_number,
        gstin: kycData?.gstin,
        account_holder_name: kycData?.account_holder_name,
        bank_name: kycData?.bank_name,
        account_type: kycData?.account_type,
        account_number: kycData?.account_number,
        confirm_account_number: kycData?.account_number,
        branch_name: kycData?.branch_name,
        ifsc_code: kycData?.ifsc_code,
      });
    }
  }, [isEditMode, user?.pendingKycRequest, form]);

  const onFinish = async (values: any) => {
    await handleSubmit(values);
  };

  const formValues = Form.useWatch([], form);

  const hasBlankChequeFile = formValues?.blank_cheque_file?.length > 0;
  const blankChequeUploaded =
    hasBlankChequeFile &&
    (formValues.blank_cheque_file[0]?.status === "done" ||
      formValues.blank_cheque_file[0]?.uid);

  const hasPanCardFile = formValues?.pan_card_file?.length > 0;
  const panCardUploaded =
    hasPanCardFile &&
    (formValues.pan_card_file[0]?.status === "done" ||
      formValues.pan_card_file[0]?.uid);

  // In edit mode, files are optional (user can keep existing files)
  // In new mode, files are required
  const fileValidation = editMode
    ? true
    : blankChequeUploaded && panCardUploaded;

  const isFormValid = !!(
    formValues?.account_holder_name &&
    formValues?.bank_name &&
    formValues?.account_number &&
    formValues?.confirm_account_number &&
    formValues?.account_number === formValues?.confirm_account_number &&
    formValues?.branch_name &&
    formValues?.ifsc_code &&
    formValues?.account_type &&
    fileValidation &&
    formValues?.pan_name &&
    formValues?.pan_number
  );

  const uploadProps = (fieldLabel: string) => ({
    name: "file",
    multiple: false,
    maxCount: 1,
    accept: "image/jpeg,image/jpg,image/png,image/svg+xml,application/pdf",
    beforeUpload: (file: File) => {
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/svg+xml", "application/pdf"];
      const isValidType = allowedTypes.includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5 MB
      if (!isValidType) {
        message.error(`${file.name} must be JPG, JPEG, PNG, SVG or PDF`);
      }
      if (!isValidSize) {
        message.error(`${file.name} exceeds 5 MB size limit`);
      }
      return isValidType && isValidSize ? true : Upload.LIST_IGNORE;
    },
    customRequest: ({ file, onSuccess, onError, onProgress }: any) => {
      const formData = new FormData();
      formData.append("attachment[0]", file as File);
      axios
        .post(
          `${import.meta.env.VITE_APP_API_URL}${ENDPOINTS.attachment}`,
          formData,
          {
            withCredentials: true,
            onUploadProgress: ({ total, loaded }) => {
              if (total) onProgress?.({ percent: (loaded / total) * 100 });
            },
          },
        )
        .then((res) => onSuccess?.(res.data?.data))
        .catch((err) => {
          message.error(
            err?.response?.data?.message || `${fieldLabel} upload failed`,
          );
          onError?.(err);
        });
    },
  });

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={bankDetails}
    >
      <Form.Item
        label={"Name on PAN Card"}
        name="pan_name"
        rules={[
          {
            required: true,
            pattern: /^[A-Za-z ]+$/,
            message: "Name on PAN Card is required",
          },
        ]}
      >
        <Input placeholder={"Enter name on PAN Card"} />
      </Form.Item>

      <Form.Item
        label="PAN Number"
        name="pan_number"
        rules={[
          {
            required: true,
            pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
            message: "PAN Number is required",
          },
        ]}
      >
        <Input
          placeholder={"Enter PAN Number"}
          onChange={(e) =>
            form.setFieldValue("pan_number", e.target.value.toUpperCase())
          }
        />
      </Form.Item>

      <Form.Item label="GSTIN (Optional)" name="gstin">
        <Input placeholder="e.g. 22AAAAA0000A1Z5" maxLength={15} />
      </Form.Item>

      <Form.Item
        label="Account Holder Name"
        name="account_holder_name"
        rules={[{ required: true, message: "Account holder name is required" }]}
      >
        <Input placeholder="Enter account holder name" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Bank Name"
            name="bank_name"
            rules={[{ required: true, message: "Bank name is required" }]}
          >
            <Input placeholder="Enter bank name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Account Type"
            name="account_type"
            rules={[{ required: true, message: "Account type is required" }]}
          >
            <Select placeholder="Select account type">
              <Option value="savings">Savings</Option>
              <Option value="current">Current</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Account Number"
            name="account_number"
            rules={[{ required: true, message: "Account number is required" }]}
          >
            <Input.Password placeholder="Enter account number" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Confirm Account Number"
            name="confirm_account_number"
            dependencies={["account_number"]}
            rules={[
              {
                required: true,
                message: "Please confirm your account number",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("account_number") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Account numbers do not match"),
                  );
                },
              }),
            ]}
          >
            <Input placeholder="Confirm account number" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Branch Name"
            name="branch_name"
            rules={[{ required: true, message: "Branch name is required" }]}
          >
            <Input placeholder="Enter branch name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="IFSC Code"
            name="ifsc_code"
            rules={[
              { required: true, message: "IFSC code is required" },
              {
                pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/,
                message: "Invalid IFSC code format",
              },
            ]}
          >
            <Input placeholder="e.g. HDFC0001234" maxLength={11} />
          </Form.Item>
        </Col>
      </Row>

      {/* Blank Cheque Upload */}

      <Row gutter={16}>
        <Col xs={{ span: 24 }} md={{ span: 12 }}>
          <Form.Item
            label="Blank Cheque"
            name="blank_cheque_file"
            rules={[
              {
                required: !editMode,
                message: "Please upload a blank cheque",
              },
            ]}
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
          >
            <Dragger {...uploadProps("Blank Cheque")}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag to upload</p>
              <p className="ant-upload-hint">
                JPG, PNG or PDF, max 1 file
                {editMode && " (optional)"}
              </p>
            </Dragger>
          </Form.Item>
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 12 }}>
          {/* PAN Card Upload */}
          <Form.Item
            label="PAN Card"
            name="pan_card_file"
            rules={[
              {
                required: !editMode,
                message: "Please upload your PAN card",
              },
            ]}
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
          >
            <Dragger {...uploadProps("PAN card")}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag to upload</p>
              <p className="ant-upload-hint">
                JPG, PNG or PDF, max 1 file
                {editMode && " (optional)"}
              </p>
            </Dragger>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item className="mt-8 ">
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={bankLoading}
          disabled={!isFormValid || bankLoading}
        >
          {editMode ? "Update KYC" : "Submit KYC"}
        </Button>

        {editMode && onCancel && (
          <Button size="large" onClick={onCancel} className="ml-4">
            Cancel
          </Button>
        )}
      </Form.Item>
    </Form>
  );
};

export default KycForm;
